<template>
  <el-row>
    <el-col :span="24">
      <h3 class="form-title">
        {{ id ? "Soliq xarajatini tahrirlash" : "Soliq xarajati qo'shish" }}
      </h3>
    </el-col>
    <el-col :span="24">
      <el-form
        v-loading="loading"
        label-position="top"
        ref="formRef"
        :model="form"
        class="tax-form"
      >
        <el-form-item
          label="Turi"
          prop="type"
          :rules="[{ required: true, message: 'Turini tanlang' }]"
        >
          <el-select
            v-model="form.type"
            placeholder="Turini tanlang"
            style="width: 100%"
          >
            <el-option value="EXPENDITURE" label="Xarajat" />
            <el-option value="PRIVILEGE" label="Imtiyoz" />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Davr turi"
          prop="period_type"
          :rules="[{ required: true, message: 'Davr turini tanlang' }]"
        >
          <el-select
            v-model="form.period_type"
            placeholder="Davr turini tanlang"
            style="width: 100%"
            @change="onPeriodTypeChange"
          >
            <el-option value="YEARLY" label="Yillik" />
            <el-option value="QUARTERLY" label="Choraklik" />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Yil"
          prop="year"
          :rules="[{ required: true, message: 'Yilni kiriting' }]"
        >
          <el-input-number v-model="form.year" :min="1900" :max="2100" style="width: 100%" />
        </el-form-item>

        <el-form-item
          v-if="form.period_type === 'QUARTERLY'"
          label="Chorak"
          prop="quarter"
          :rules="[{ required: true, message: 'Chorakni tanlang' }]"
        >
          <el-select v-model="form.quarter" placeholder="Chorakni tanlang" style="width: 100%">
            <el-option :value="1" label="1-chorak" />
            <el-option :value="2" label="2-chorak" />
            <el-option :value="3" label="3-chorak" />
            <el-option :value="4" label="4-chorak" />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Excel fayl"
          prop="excel_file_id"
          :rules="[{ validator: validateAtLeastOne, trigger: 'change' }]"
        >
          <el-input v-model="form.excel_file_id" style="display: none" />
          <input
            type="file"
            ref="excelFileInputRef"
            hidden
            accept=".xls,.xlsx"
            @change="(e) => uploadFile(e, 'excel')"
          />
          <div class="file-upload-row">
            <el-button :loading="excelFileLoading" @click="excelFileInputRef.click()">
              {{ form.excel_file_id ? "Excel faylni almashtirish" : "Excel fayl yuklash" }}
            </el-button>
            <span v-if="excelFileName" class="file-name">{{ excelFileName }}</span>
          </div>
        </el-form-item>

        <el-form-item
          label="SDMX fayl"
          prop="sdmx_file_id"
          :rules="[{ validator: validateAtLeastOne, trigger: 'change' }]"
        >
          <el-input v-model="form.sdmx_file_id" style="display: none" />
          <input
            type="file"
            ref="sdmxFileInputRef"
            hidden
            accept=".xml,.sdmx"
            @change="(e) => uploadFile(e, 'sdmx')"
          />
          <div class="file-upload-row">
            <el-button :loading="sdmxFileLoading" @click="sdmxFileInputRef.click()">
              {{ form.sdmx_file_id ? "SDMX faylni almashtirish" : "SDMX fayl yuklash" }}
            </el-button>
            <span v-if="sdmxFileName" class="file-name">{{ sdmxFileName }}</span>
          </div>
        </el-form-item>

        <el-form-item
          label="PDF fayl"
          prop="pdf_file_id"
          :rules="[{ validator: validateAtLeastOne, trigger: 'change' }]"
        >
          <el-input v-model="form.pdf_file_id" style="display: none" />
          <input
            type="file"
            ref="pdfFileInputRef"
            hidden
            accept=".pdf"
            @change="(e) => uploadFile(e, 'pdf')"
          />
          <div class="file-upload-row">
            <el-button :loading="pdfFileLoading" @click="pdfFileInputRef.click()">
              {{ form.pdf_file_id ? "PDF faylni almashtirish" : "PDF fayl yuklash" }}
            </el-button>
            <span v-if="pdfFileName" class="file-name">{{ pdfFileName }}</span>
          </div>
          <div class="file-hint">Kamida bitta fayl (Excel, SDMX yoki PDF) yuklanishi shart</div>
        </el-form-item>

        <div class="form-actions">
          <router-link to="/app/tax-expenditures" class="el-button el-button--danger">
            Bekor qilish
          </router-link>
          <el-button type="primary" :loading="submitLoading" @click="submitForm">
            Saqlash
          </el-button>
        </div>
      </el-form>
    </el-col>
  </el-row>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage, ElNotification } from "element-plus";
import http from "@/http";
import store from "@/store";
import router from "@/router";
import { useRoute } from "vue-router";

const route = useRoute();
const { id } = route.params;

const formRef = ref();
const excelFileInputRef = ref();
const sdmxFileInputRef = ref();
const pdfFileInputRef = ref();
const loading = ref(false);
const excelFileLoading = ref(false);
const sdmxFileLoading = ref(false);
const pdfFileLoading = ref(false);
const submitLoading = ref(false);
const excelFileName = ref(null);
const sdmxFileName = ref(null);
const pdfFileName = ref(null);

const form = reactive({
  type: "EXPENDITURE",
  period_type: "YEARLY",
  year: new Date().getFullYear(),
  quarter: null,
  excel_file_id: "",
  sdmx_file_id: "",
  pdf_file_id: "",
});

const onPeriodTypeChange = () => {
  form.quarter = null;
};

const validateAtLeastOne = (_, __, callback) => {
  if (!form.excel_file_id && !form.sdmx_file_id && !form.pdf_file_id) {
    callback(new Error("Kamida bitta fayl (Excel, SDMX yoki PDF) yuklanishi shart"));
  } else {
    callback();
  }
};

const EXT_MAP = {
  excel: [".xls", ".xlsx"],
  sdmx: [".xml", ".sdmx"],
  pdf: [".pdf"],
};
const ERROR_MAP = {
  excel: "Faqat Excel fayl (.xls, .xlsx) yuklanishi mumkin!",
  sdmx: "Faqat SDMX fayl (.xml, .sdmx) yuklanishi mumkin!",
  pdf: "Faqat PDF fayl (.pdf) yuklanishi mumkin!",
};

const uploadFile = (event, type) => {
  const file = event.target.files[0];
  if (!file) return;
  const ext = "." + file.name.split(".").pop().toLowerCase();
  if (!EXT_MAP[type].includes(ext)) {
    ElMessage.error(ERROR_MAP[type]);
    event.target.value = "";
    return;
  }
  if (file.size / 1024 / 1024 > 20) {
    ElMessage.error("Fayl xajmi max. 20MB bo'lishi kerak!");
    event.target.value = "";
    return;
  }
  const formData = new FormData();
  formData.append("file", file);
  if (type === "excel") excelFileLoading.value = true;
  else if (type === "sdmx") sdmxFileLoading.value = true;
  else pdfFileLoading.value = true;
  http
    .post(`${process.env.VUE_APP_BASE_API_URL}api/v2/info/file/upload`, formData, {
      headers: {
        Authorization: store.state.accessToken,
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => {
      if (type === "excel") {
        form.excel_file_id = data.hashId;
        excelFileName.value = file.name;
      } else if (type === "sdmx") {
        form.sdmx_file_id = data.hashId;
        sdmxFileName.value = file.name;
      } else {
        form.pdf_file_id = data.hashId;
        pdfFileName.value = file.name;
      }
      formRef.value?.clearValidate(["excel_file_id", "sdmx_file_id", "pdf_file_id"]);
    })
    .catch(() => {
      ElMessage.error("Fayl yuklashda xatolik. Qayta urinib ko'ring!");
    })
    .finally(() => {
      if (type === "excel") excelFileLoading.value = false;
      else if (type === "sdmx") sdmxFileLoading.value = false;
      else pdfFileLoading.value = false;
      event.target.value = "";
    });
};

const submitForm = () => {
  if (!formRef.value) return;
  formRef.value.validate((valid, fields) => {
    if (!valid) {
      Object.values(fields)
        .flat()
        .forEach((e) => ElMessage.error(e.message));
      return;
    }
    submitLoading.value = true;
    const url = id ? `api/v1/tax-expenditures/${id}` : "api/v1/tax-expenditures";
    const method = id ? "put" : "post";
    const payload = {
      type: form.type,
      period_type: form.period_type,
      year: form.year,
      excel_file_id: form.excel_file_id,
      sdmx_file_id: form.sdmx_file_id,
      pdf_file_id: form.pdf_file_id,
    };
    if (form.period_type === "QUARTERLY") payload.quarter = form.quarter;
    http({ url, data: payload, method })
      .then(() => {
        ElMessage({ type: "success", message: "Muvaffaqqiyatli saqlandi" });
        router.push("/app/tax-expenditures");
      })
      .catch(({ response }) => {
        ElNotification.error({
          message: response?.data?.message || "Xatolik, qayta urinib ko'ring",
        });
      })
      .finally(() => {
        submitLoading.value = false;
      });
  });
};

onMounted(() => {
  if (!id) return;
  loading.value = true;
  http
    .get(`api/v1/tax-expenditures/${id}`)
    .then(({ data }) => {
      form.type = data.type;
      form.period_type = data.period_type;
      form.year = data.year;
      form.quarter = data.quarter;
      form.excel_file_id = data.excel_file_id || "";
      form.sdmx_file_id = data.sdmx_file_id || "";
      form.pdf_file_id = data.pdf_file_id || "";
      excelFileName.value = data.excel_file_name || null;
      sdmxFileName.value = data.sdmx_file_name || null;
      pdfFileName.value = data.pdf_file_name || null;
    })
    .catch(() => {
      ElMessage.error("Ma'lumot yuklashda xatolik!");
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style scoped lang="scss">
.form-title {
  margin-bottom: 20px;
  font-weight: 600;
}
.tax-form {
  max-width: 600px;
}
.file-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.file-name {
  color: #606266;
  font-size: 14px;
}
.file-hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  a {
    text-decoration: none;
  }
}
</style>
